import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wrench, Plus, Search, ArrowLeft, Trash2, Edit, Save, X,
  ArrowRightLeft, RotateCcw, Clock, User, Camera, Filter, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CONDITION_LABELS = {
  bueno: { label: 'Bueno', color: 'bg-green-100 text-green-700' },
  dañado: { label: 'Dañado', color: 'bg-red-100 text-red-700' },
  falta_piezas: { label: 'Falta de piezas', color: 'bg-amber-100 text-amber-700' }
};

export default function ToolsManager() {
  const { storeDomain, adminPath } = useParams();
  const { user, getAuthHeader, logout, getAdminBasePath, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());

  const [tab, setTab] = useState('tools'); // tools, loans, history
  const [tools, setTools] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Tool form
  const [showForm, setShowForm] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', photo: '', total_quantity: 1 });
  // Loan form
  const [showLoanForm, setShowLoanForm] = useState(false);
  const [loanTool, setLoanTool] = useState(null);
  const [loanData, setLoanData] = useState({ borrower_name: '', observations: '', initial_photo: '' });
  // Return form
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returningLoan, setReturningLoan] = useState(null);
  const [returnData, setReturnData] = useState({ return_condition: 'bueno', observations: '', return_photo: '' });
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBorrower, setFilterBorrower] = useState('');

  const loadTools = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tools`, { headers: getAuthHeader() });
      setTools(res.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const loadLoans = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterBorrower) params.append('borrower', filterBorrower);
      const res = await axios.get(`${API}/tools/loans?${params}`, { headers: getAuthHeader() });
      setLoans(res.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader, filterStatus, filterBorrower]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`${basePath}/login`); return; }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    setLoading(true);
    Promise.all([loadTools(), loadLoans()]).finally(() => setLoading(false));
  }, [user, authLoading, loadTools, loadLoans]);

  const handleSaveTool = async () => {
    if (!formData.name || !formData.code) { toast.error('Nombre y código son requeridos'); return; }
    try {
      if (editingTool) {
        await axios.put(`${API}/tools/${editingTool.id}`, formData, { headers: getAuthHeader() });
        toast.success('Herramienta actualizada');
      } else {
        await axios.post(`${API}/tools`, { ...formData, total_quantity: parseInt(formData.total_quantity) || 1 }, { headers: getAuthHeader() });
        toast.success('Herramienta creada');
      }
      setShowForm(false);
      setEditingTool(null);
      setFormData({ name: '', code: '', description: '', photo: '', total_quantity: 1 });
      loadTools();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Error al guardar'); }
  };

  const handleDeleteTool = async (id) => {
    if (!window.confirm('¿Eliminar esta herramienta?')) return;
    try {
      await axios.delete(`${API}/tools/${id}`, { headers: getAuthHeader() });
      toast.success('Eliminada');
      loadTools();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Error al eliminar'); }
  };

  const handleLoan = async () => {
    if (!loanData.borrower_name) { toast.error('Ingrese quién lleva la herramienta'); return; }
    try {
      await axios.post(`${API}/tools/${loanTool.id}/loan`, loanData, { headers: getAuthHeader() });
      toast.success(`${loanTool.name} prestada a ${loanData.borrower_name}`);
      setShowLoanForm(false);
      setLoanTool(null);
      setLoanData({ borrower_name: '', observations: '', initial_photo: '' });
      loadTools();
      loadLoans();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Error al prestar'); }
  };

  const handleReturn = async () => {
    try {
      await axios.post(`${API}/tools/loans/${returningLoan.id}/return`, returnData, { headers: getAuthHeader() });
      toast.success(`${returningLoan.tool_name} devuelta`);
      setShowReturnForm(false);
      setReturningLoan(null);
      setReturnData({ return_condition: 'bueno', observations: '', return_photo: '' });
      loadTools();
      loadLoans();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Error al devolver'); }
  };

  const uploadPhoto = async (file, setter, field) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload-image`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setter(prev => ({ ...prev, [field]: res.data.url }));
      toast.success('Foto subida');
    } catch (e) { toast.error('Error al subir foto'); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d.endsWith?.('Z') ? d : d + 'Z');
    return date.toLocaleString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filteredTools = tools.filter(t => t.name.toLowerCase().includes(searchTerm.toLowerCase()) || t.code.toLowerCase().includes(searchTerm.toLowerCase()));
  const activeLoans = loans.filter(l => l.status === 'active');

  if (loading) return <div className="flex items-center justify-center py-20"><Wrench size={40} className="animate-spin text-accent-text" /></div>;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)' }}>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`${basePath}/dashboard`)} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={20} /></button>
          <Wrench size={24} className="text-accent-text" />
          <h1 className="text-xl font-bold">Herramientas</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {[
            { key: 'tools', label: 'Inventario', icon: Wrench },
            { key: 'loans', label: `Préstamos Activos (${activeLoans.length})`, icon: ArrowRightLeft },
            { key: 'history', label: 'Historial', icon: Clock }
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); if (t.key === 'history') setFilterStatus(''); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all"
              style={{ background: tab === t.key ? 'var(--accent-primary)' : 'white', color: tab === t.key ? 'white' : 'var(--text-primary)', border: `1px solid ${tab === t.key ? 'var(--accent-primary)' : 'var(--border-light)'}` }}
              data-testid={`tools-tab-${t.key}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* TAB: Inventario */}
        {tab === 'tools' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar herramienta..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="tools-search" />
              </div>
              <button onClick={() => { setShowForm(true); setEditingTool(null); setFormData({ name: '', code: '', description: '', photo: '', total_quantity: 1 }); }}
                className="btn-primary text-sm px-4 flex items-center gap-1" data-testid="add-tool-btn">
                <Plus size={16} /> Nueva
              </button>
            </div>

            {filteredTools.length === 0 ? (
              <div className="bg-white rounded-xl border p-8 text-center" style={{ borderColor: 'var(--border-light)' }}>
                <Wrench size={48} className="mx-auto text-text-muted mb-3" />
                <p className="text-text-muted">No hay herramientas registradas</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredTools.map(tool => (
                  <div key={tool.id} className="bg-white rounded-xl border p-4" style={{ borderColor: 'var(--border-light)' }} data-testid={`tool-card-${tool.id}`}>
                    <div className="flex gap-3">
                      {tool.photo ? (
                        <img src={tool.photo} alt={tool.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Wrench size={24} className="text-gray-400" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm truncate">{tool.name}</h3>
                        <p className="text-xs text-text-muted">Código: {tool.code}</p>
                        {tool.description && <p className="text-xs text-text-muted truncate mt-0.5">{tool.description}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tool.available_quantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {tool.available_quantity}/{tool.total_quantity} disponible{tool.available_quantity !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => { setLoanTool(tool); setShowLoanForm(true); setLoanData({ borrower_name: '', observations: '', initial_photo: '' }); }}
                        disabled={tool.available_quantity <= 0}
                        className="flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 disabled:opacity-40 btn-primary" data-testid={`loan-tool-${tool.id}`}>
                        <ArrowRightLeft size={14} /> Prestar
                      </button>
                      {user?.role === 'admin' && (
                        <>
                          <button onClick={() => { setEditingTool(tool); setFormData({ name: tool.name, code: tool.code, description: tool.description || '', photo: tool.photo || '', total_quantity: tool.total_quantity }); setShowForm(true); }}
                            className="p-2 rounded-lg border hover:bg-gray-50" style={{ borderColor: 'var(--border-light)' }}><Edit size={14} /></button>
                          <button onClick={() => handleDeleteTool(tool.id)} className="p-2 rounded-lg border hover:bg-red-50 text-red-500" style={{ borderColor: 'var(--border-light)' }}><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: Préstamos Activos */}
        {tab === 'loans' && (
          <div className="space-y-3">
            {activeLoans.length === 0 ? (
              <div className="bg-white rounded-xl border p-8 text-center" style={{ borderColor: 'var(--border-light)' }}>
                <Check size={48} className="mx-auto text-green-400 mb-3" />
                <p className="text-text-muted">No hay préstamos activos</p>
              </div>
            ) : activeLoans.map(loan => (
              <div key={loan.id} className="bg-white rounded-xl border p-4" style={{ borderColor: 'var(--border-light)' }} data-testid={`active-loan-${loan.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-sm">{loan.tool_name}</h3>
                    <p className="text-xs text-text-muted">Código: {loan.tool_code}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <User size={12} className="text-text-muted" />
                      <span className="text-sm font-medium">Entregado a: {loan.borrower_name}</span>
                    </div>
                    <p className="text-xs text-text-muted">Registró: <strong>{loan.created_by || 'Sin registro'}</strong></p>
                    <p className="text-xs text-text-muted mt-1"><Clock size={11} className="inline mr-1" />{formatDate(loan.loan_date)}</p>
                    {loan.initial_observations && <p className="text-xs mt-1 italic text-amber-700">Nota: {loan.initial_observations}</p>}
                  </div>
                  {loan.initial_photo && <img src={loan.initial_photo} alt="Estado inicial" className="w-14 h-14 rounded-lg object-cover" />}
                </div>
                <button onClick={() => { setReturningLoan(loan); setShowReturnForm(true); setReturnData({ return_condition: 'bueno', observations: '', return_photo: '' }); }}
                  className="w-full mt-3 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 btn-primary" data-testid={`return-loan-${loan.id}`}>
                  <RotateCcw size={16} /> Registrar Devolución
                </button>
              </div>
            ))}
          </div>
        )}

        {/* TAB: Historial */}
        {tab === 'history' && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); }}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="filter-status">
                <option value="">Todos los estados</option>
                <option value="active">Activos</option>
                <option value="returned">Devueltos</option>
              </select>
              <div className="flex-1 relative">
                <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input type="text" value={filterBorrower} onChange={e => setFilterBorrower(e.target.value)} placeholder="Filtrar por empleado..."
                  className="w-full pl-8 pr-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="filter-borrower" />
              </div>
              <button onClick={loadLoans} className="px-3 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50" style={{ borderColor: 'var(--border-light)' }}>
                <Search size={14} />
              </button>
            </div>

            {loans.length === 0 ? (
              <div className="bg-white rounded-xl border p-8 text-center" style={{ borderColor: 'var(--border-light)' }}>
                <Clock size={48} className="mx-auto text-text-muted mb-3" />
                <p className="text-text-muted">No hay movimientos</p>
              </div>
            ) : (
              <div className="space-y-2">
                {loans.map(loan => (
                  <div key={loan.id} className="bg-white rounded-xl border p-4" style={{ borderColor: 'var(--border-light)' }}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-sm">{loan.tool_name}</h3>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${loan.status === 'active' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                            {loan.status === 'active' ? 'PRESTADO' : 'DEVUELTO'}
                          </span>
                          {loan.return_condition && CONDITION_LABELS[loan.return_condition] && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CONDITION_LABELS[loan.return_condition].color}`}>
                              {CONDITION_LABELS[loan.return_condition].label}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-muted mt-0.5"><User size={11} className="inline mr-1" />Entregado a: {loan.borrower_name}</p>
                        <p className="text-xs text-text-muted">Registró: <strong>{loan.created_by || 'Sin registro'}</strong></p>
                        <p className="text-xs text-text-muted"><ArrowRightLeft size={11} className="inline mr-1" />Prestado: {formatDate(loan.loan_date)}</p>
                        {loan.return_date && <p className="text-xs text-text-muted"><RotateCcw size={11} className="inline mr-1" />Devuelto: {formatDate(loan.return_date)}</p>}
                        {loan.initial_observations && <p className="text-xs italic text-text-muted mt-1">Salida: {loan.initial_observations}</p>}
                        {loan.return_observations && <p className="text-xs italic text-amber-700 mt-0.5">Devolución: {loan.return_observations}</p>}
                      </div>
                      <div className="flex gap-1">
                        {loan.initial_photo && <img src={loan.initial_photo} alt="Inicio" className="w-10 h-10 rounded object-cover" />}
                        {loan.return_photo && <img src={loan.return_photo} alt="Devolución" className="w-10 h-10 rounded object-cover border-2 border-green-300" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal: Tool Form */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowForm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6" data-testid="tool-form-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">{editingTool ? 'Editar Herramienta' : 'Nueva Herramienta'}</h3>
                <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
              </div>
              <div className="space-y-3">
                <input type="text" placeholder="Nombre *" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="tool-name-input" />
                <input type="text" placeholder="Código *" value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="tool-code-input" />
                <textarea placeholder="Descripción" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} rows={2} />
                <div>
                  <label className="block text-sm font-medium mb-1">Cantidad total</label>
                  <input type="number" min="1" value={formData.total_quantity} onChange={e => setFormData({ ...formData, total_quantity: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="tool-quantity-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Foto (opcional)</label>
                  <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0], setFormData, 'photo')}
                    className="w-full text-sm" />
                  {formData.photo && <img src={formData.photo} alt="Preview" className="w-20 h-20 rounded-lg object-cover mt-2" />}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowForm(false)} className="flex-1 btn-secondary text-sm">Cancelar</button>
                <button onClick={handleSaveTool} className="flex-1 btn-primary text-sm" data-testid="save-tool-btn">
                  <Save size={16} className="mr-1" /> {editingTool ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal: Loan Form */}
      {showLoanForm && loanTool && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowLoanForm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6" data-testid="loan-form-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">Prestar: {loanTool.name}</h3>
                <button onClick={() => setShowLoanForm(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
              </div>
              <p className="text-xs text-text-muted mb-3">Disponibles: {loanTool.available_quantity}/{loanTool.total_quantity}</p>
              <div className="p-2.5 rounded-lg mb-3 text-sm" style={{ background: 'var(--bg-section)' }}>
                <span className="text-text-muted">Registrado por: </span>
                <span className="font-bold">{user?.username}</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-text-muted">Entregado a (cliente, compañero, etc.) *</label>
                  <input type="text" placeholder="Nombre de quien recibe" value={loanData.borrower_name}
                    onChange={e => setLoanData({ ...loanData, borrower_name: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="loan-borrower-input" />
                </div>
                <textarea placeholder="Observaciones (estado inicial, notas)" value={loanData.observations}
                  onChange={e => setLoanData({ ...loanData, observations: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} rows={2} />
                <div>
                  <label className="block text-sm font-medium mb-1"><Camera size={14} className="inline mr-1" />Foto estado inicial (opcional)</label>
                  <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0], setLoanData, 'initial_photo')}
                    className="w-full text-sm" />
                  {loanData.initial_photo && <img src={loanData.initial_photo} alt="Preview" className="w-20 h-20 rounded-lg object-cover mt-2" />}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowLoanForm(false)} className="flex-1 btn-secondary text-sm">Cancelar</button>
                <button onClick={handleLoan} className="flex-1 btn-primary text-sm" data-testid="confirm-loan-btn">
                  <ArrowRightLeft size={16} className="mr-1" /> Registrar Préstamo
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal: Return Form */}
      {showReturnForm && returningLoan && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowReturnForm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6" data-testid="return-form-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">Devolver: {returningLoan.tool_name}</h3>
                <button onClick={() => setShowReturnForm(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
              </div>
              <p className="text-xs text-text-muted mb-3">Prestado a: {returningLoan.borrower_name} | {formatDate(returningLoan.loan_date)}</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-2">Estado de la herramienta</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(CONDITION_LABELS).map(([key, { label, color }]) => (
                      <button key={key} onClick={() => setReturnData({ ...returnData, return_condition: key })}
                        className={`py-2 px-2 rounded-lg border text-xs font-medium text-center transition-all ${returnData.return_condition === key ? color + ' border-current' : ''}`}
                        style={{ borderColor: returnData.return_condition === key ? undefined : 'var(--border-light)' }}
                        data-testid={`condition-${key}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea placeholder="Observaciones de devolución" value={returnData.observations}
                  onChange={e => setReturnData({ ...returnData, observations: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} rows={2} />
                <div>
                  <label className="block text-sm font-medium mb-1"><Camera size={14} className="inline mr-1" />Foto estado de regreso (opcional)</label>
                  <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0], setReturnData, 'return_photo')}
                    className="w-full text-sm" />
                  {returnData.return_photo && <img src={returnData.return_photo} alt="Preview" className="w-20 h-20 rounded-lg object-cover mt-2" />}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowReturnForm(false)} className="flex-1 btn-secondary text-sm">Cancelar</button>
                <button onClick={handleReturn} className="flex-1 btn-primary text-sm" data-testid="confirm-return-btn">
                  <RotateCcw size={16} className="mr-1" /> Confirmar Devolución
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
